import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-access-denied-panel>` says about itself.
 *
 * Three pieces in this group all say "this did not work" and are told apart by WHOSE fault it is
 * and what the person can do next. This one is the REFUSAL: the request was understood, the caller
 * is simply not granted it. Nothing is broken, so the panel is deliberately neutral rather than
 * danger-tinted — the lock disc reads the info tint and the rest of it reads the ordinary surface
 * and text tokens.
 *
 * The `grant` line is the whole reason the piece exists rather than a sentence of prose: it names
 * THE PERMISSION the caller would need, which is a fact about the caller, and never the content
 * that was withheld, which is a fact about the record. Getting that the wrong way round turns a
 * blocked page into a disclosure — "You cannot see the discount on ORD-4471" has already said there
 * is a discount on ORD-4471. The panel gives the safe half a place to go so the copy does not have
 * to improvise it.
 *
 * The old hand-kept example set `grant` and nothing else, so it drew the default heading over an
 * empty explanation. Both examples here carry the explanation the panel was designed around.
 */
const docs: ComponentDocs = {
  aliases: ['permission denied', 'forbidden', '403 page', 'no access screen', 'not authorised'],

  answers: [
    'tell somebody they are not allowed to see this',
    'say which permission they would need',
    'give a blocked person somewhere to go next',
    'block a tab without pretending it is empty',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'heading',
      attribute: 'heading',
      type: 'text',
      description:
        'The line at the top. Left unset it reads "You don\'t have access", which is the right ' +
        'sentence often enough to be the default.',
      default: "You don't have access",
    },
    {
      name: 'grant',
      attribute: 'grant',
      type: 'text',
      description:
        'The permission the caller would need, drawn as a mono line reading "Requires <grant>". ' +
        'Name the grant, never the content that was withheld. Unset, the line is not drawn at all.',
    },
  ],

  // Read from the source: this panel dispatches nothing. The way forward is whatever control the
  // author slots into `action`, and that control raises its own events.
  events: [],

  state: [],

  slots: [
    { name: '', takes: 'The explanation — why this is blocked, in a sentence.' },
    { name: 'action', takes: 'The one way forward: a request-access control, or a way back.' },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The panel fill.' },
    { token: '--caos-color-border', controls: 'The 1px panel edge and the edge of the grant line.' },
    { token: '--caos-radius-lg', controls: 'The panel corner radius.' },
    { token: '--caos-color-info-bg', controls: 'The disc behind the lock glyph.' },
    { token: '--caos-color-info', controls: 'The lock glyph itself.' },
    { token: '--caos-radius-pill', controls: 'The round lock disc.' },
    { token: '--caos-color-text', controls: 'The heading, and the grant name.' },
    { token: '--caos-color-text-muted', controls: 'The explanation, and the word "Requires".' },
    { token: '--caos-color-surface-2', controls: 'The fill behind the grant line.' },
    { token: '--caos-radius-sm', controls: 'The corners of the grant line.' },
    { token: '--caos-font-display', controls: 'The typeface the heading is set in.' },
    { token: '--caos-font', controls: 'The typeface of the word "Requires".' },
    { token: '--caos-font-mono', controls: 'The typeface the grant name is set in, so it can be quoted exactly.' },
    { token: '--caos-space-7', controls: 'The vertical padding of the panel.' },
    { token: '--caos-space-5', controls: 'The horizontal padding of the panel.' },
    { token: '--caos-space-3', controls: 'The gap between the disc, heading, explanation and grant line.' },
    { token: '--caos-space-2', controls: 'The gap between controls in the action slot.' },
    { token: '--caos-space-1', controls: 'The vertical padding of the grant line.' },
  ],

  parts: [
    { part: 'panel', is: 'the bordered panel the whole refusal sits in' },
    { part: 'icon', is: 'the lock glyph' },
    { part: 'title', is: 'the heading line' },
    { part: 'message', is: 'the explanatory text' },
    { part: 'grant', is: 'the named permission a person would have to be given' },
    { part: 'actions', is: 'the row holding whatever action was slotted in' },
  ],

  methods: [],

  placing:
    'It replaces the contents of the region it is refusing — a tab, a section, a whole page — rather ' +
    'than sitting above them, because anything left drawn beside it is content the caller was not ' +
    'granted. It centres itself in whatever width it is given and stops at 440px, so a wide region ' +
    'needs nothing done to it.',

  whenToUse:
    'A surface that loaded correctly and is being withheld: the caller is missing a grant. Reach for ' +
    'it whenever the honest answer is "you are not allowed", so a person stops looking for the button ' +
    'they think they have lost.',

  insteadUse: [
    {
      name: 'ErrorPanel',
      when:
        'The surface FAILED rather than being refused. A failure has a code and a correlation id and ' +
        'may work on a retry; a refusal has neither and will not change until somebody is granted ' +
        'something. Drawing a refusal as an error sends a person to support for a ticket nobody can ' +
        'close, and drawing a failure as a refusal sends them to their administrator instead of the ' +
        'on-call engineer.',
    },
    {
      name: 'EmptyState',
      when:
        'The caller IS allowed and there is genuinely nothing here yet. These two are the pair most ' +
        'often confused, and confusing them is the more expensive mistake in this direction: a person ' +
        'shown an empty list when they were actually blocked concludes the data is missing and goes ' +
        'looking for who deleted it.',
    },
    {
      name: 'ValidationSummary',
      when:
        'Nothing is being withheld — a save was rejected because of what was typed. That is a list of ' +
        'things the same person can fix now, not a grant somebody else has to give them.',
    },
    {
      name: 'NoticeBanner',
      when:
        'Most of the surface IS granted and only part of it is restricted. The banner sits above the ' +
        'content and says what is limited; this panel is for when there is no content to sit above.',
    },
  ],

  accessibility:
    'The panel is a labelled region — `role="region"` pointed at the heading by id — so it is reachable ' +
    'as a landmark and announces what it is, and the lock disc is hidden from assistive tech because ' +
    'the heading already carries the meaning. Deliberately NOT `role="alert"`: a refusal is a state the ' +
    'page is in, not something that just happened, and interrupting a screen reader for it would be ' +
    'wrong. What is left to the author is the copy — the explanation and the grant name are the whole ' +
    'accessible content, so a panel with a heading and nothing else announces almost nothing — and ' +
    'making sure whatever goes in the action slot is a real focusable control.',

  examples: [
    {
      key: 'missing-grant',
      label: 'The grant it needs',
      summary:
        'The blocked state as it is normally drawn: the default heading, a sentence of explanation, and ' +
        'the permission named in mono so it can be quoted to an administrator verbatim.',
      placement: {
        component: 'caos-access-denied-panel',
        inputs: { grant: 'Orders · View amounts' },
        children: ['Amounts on this order are restricted to the sales management team. Ask your administrator to grant the permission below.'],
      },
    },
    {
      key: 'request-access',
      label: 'With a way forward',
      summary:
        'The same refusal with the one action that resolves it, so the person is not left to work out ' +
        'who to ask — and with a heading written for the specific surface rather than the default.',
      placement: {
        component: 'caos-access-denied-panel',
        inputs: { heading: 'Amounts are restricted', grant: 'Orders · View amounts' },
        children: [
          'Your role can open this order and read its line items, but not the amounts on them.',
          {
            component: 'caos-button',
            inputs: { slot: 'action', variant: 'primary' },
            children: ['Request access'],
          },
          {
            component: 'caos-button',
            inputs: { slot: 'action', variant: 'ghost' },
            children: ['Back to the order'],
          },
        ],
      },
    },
  ],
};

export default docs;
